import { Component } from '@theme/component';

const storageKey = 'dosa-favorites-v1';
const safeUrl = (value) => {
  try {
    const url = new URL(value, location.origin);
    return ['https:', 'http:'].includes(url.protocol) ? url.href : '';
  } catch { return ''; }
};

class DosaSavedItems extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.setup();
  }

  setup() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.savedDialog = this.querySelector('[data-saved-dialog]');
    this.photoDialog = this.querySelector('[data-photo-dialog]');
    this.items = this.read();
    document.addEventListener('click', (event) => this.handleClick(event), options);
    window.addEventListener('storage', (event) => {
      if (event.key === storageKey) { this.items = this.read(); this.refresh(); }
    }, options);
    for (const dialog of [this.savedDialog, this.photoDialog]) {
      if (!dialog) continue;
      dialog.addEventListener('close', () => {
        const opener = dialog === this.savedDialog ? this.savedOpener : this.photoOpener;
        if (opener?.isConnected) opener.focus();
      }, options);
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      }, options);
    }
    this.refresh();
  }

  read() {
    try {
      const data = JSON.parse(localStorage.getItem(storageKey) || '[]');
      return Array.isArray(data) ? data.filter((item) => typeof item.id === 'string' && typeof item.title === 'string' && safeUrl(item.image) && safeUrl(item.url)).slice(0, 100) : [];
    } catch { return []; }
  }

  itemFrom(button) {
    try {
      const item = JSON.parse(button.dataset.item);
      if (typeof item.id !== 'string' || typeof item.title !== 'string') return null;
      item.image = safeUrl(item.image);
      item.url = safeUrl(item.url);
      return item.image && item.url ? item : null;
    } catch { return null; }
  }

  handleClick(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-saved-open], [data-saved-toggle], [data-saved-remove], [data-saved-close], [data-photo-open], [data-photo-close]');
    if (!button) return;
    if (button.hasAttribute('data-saved-close')) { this.savedDialog?.close(); return; }
    if (button.hasAttribute('data-photo-close')) { this.photoDialog?.close(); return; }
    if (button.hasAttribute('data-saved-open')) {
      this.savedOpener = button;
      this.renderSaved();
      if (this.savedDialog && !this.savedDialog.open) this.savedDialog.showModal();
      return;
    }
    if (button.hasAttribute('data-photo-open')) {
      const item = this.itemFrom(button);
      if (!item || !this.photoDialog) return;
      this.photoDialog.querySelector('[data-photo-title]').textContent = item.title;
      const image = this.photoDialog.querySelector('[data-photo-image]');
      image.src = item.image;
      image.alt = item.title;
      this.photoOpener = button;
      if (!this.photoDialog.open) this.photoDialog.showModal();
      return;
    }
    if (button.hasAttribute('data-saved-remove')) {
      this.items = this.items.filter((item) => item.id !== button.dataset.savedRemove);
    } else {
      const item = this.itemFrom(button);
      if (!item) return;
      if (this.items.some((saved) => saved.id === item.id)) this.items = this.items.filter((saved) => saved.id !== item.id);
      else if (this.items.length < 100) this.items.push(item);
    }
    try { localStorage.setItem(storageKey, JSON.stringify(this.items)); } catch { /* Keep preferences available for this page if storage is disabled. */ }
    this.refresh();
    if (this.savedDialog?.open) {
      this.renderSaved();
      if (button.hasAttribute('data-saved-remove')) {
        (this.savedDialog.querySelector('[data-saved-remove]') || this.savedDialog.querySelector('[data-saved-close]'))?.focus();
      }
    }
  }

  refresh() {
    this.querySelectorAll('[data-saved-count]').forEach((element) => { element.textContent = String(this.items.length); });
    document.querySelectorAll('[data-saved-toggle]').forEach((button) => {
      const item = this.itemFrom(button);
      button.setAttribute('aria-pressed', String(Boolean(item && this.items.some((saved) => saved.id === item.id))));
    });
  }

  renderSaved() {
    const list = this.savedDialog?.querySelector('[data-saved-list]');
    if (!list) return;
    list.replaceChildren();
    this.savedDialog.querySelector('[data-saved-empty]').hidden = this.items.length > 0;
    for (const item of this.items) {
      const row = document.createElement('li');
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.title;
      image.width = 76;
      image.height = 96;
      const name = document.createElement('a');
      name.href = item.url;
      name.textContent = item.title;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.dataset.savedRemove = item.id;
      remove.textContent = this.dataset.removeLabel;
      remove.className = 'dosa-saved-remove';
      remove.setAttribute('aria-label', `${this.dataset.removeLabel}: ${item.title}`);
      row.append(image, name, remove);
      list.append(row);
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.savedDialog?.close();
    this.photoDialog?.close();
    super.disconnectedCallback();
  }
}

if (!customElements.get('dosa-saved-items')) customElements.define('dosa-saved-items', DosaSavedItems);
